import { useCallback, useEffect, useState } from "react";
import type { ActivityEntry, AdminUserRow, SessionSummary } from "../types";
import { useInfinite, type Page } from "../hooks/useInfinite";
import { SkeletonList } from "./Skeleton";
import { BrandingSettings } from "./BrandingSettings";
import { UserPermissionsForm } from "./UserPermissionsForm";
import { InvitesPanel } from "./InvitesPanel";
import { BillingPanel } from "./BillingPanel";
import { OrganizationsPanel } from "./OrganizationsPanel";
import { OrgIdentityPanel } from "./OrgIdentityPanel";
import { InstructionsPanel } from "./InstructionsPanel";
import { SsoPanel } from "./SsoPanel";
import { RetentionPanel } from "./RetentionPanel";
import { ApiPanel } from "./ApiPanel";
import { OverviewPanel } from "./OverviewPanel";
import { PlansPanel } from "./PlansPanel";
import { EngineAuthPanel } from "./EngineAuthPanel";
import { ReposPage } from "./ReposPage";
import { OrgSwitcher } from "./OrgSwitcher";
import { AuditPanel } from "./AuditPanel";
import { Pagination, PAGE_SIZE } from "./Pagination";
import { ConfirmModal, PromptModal } from "./ConfirmModal";
import { showToast } from "./toast";
import { AdminNoteEditor } from "./AdminNoteEditor";

interface Props {
  onClose: () => void;
  onOpenSession: (id: string) => void;
  selectedUserId: string | null;
  onSelectUser: (id: string | null) => void;
  isSysadmin: boolean;
}

const ACTIVITY_LABEL: Record<ActivityEntry["type"], string> = {
  register: "cadastrou-se",
  login: "entrou",
  login_failed: "login recusado",
  login_blocked: "login bloqueado",
  logout: "saiu",
  ask: "perguntou",
  regenerate: "regenerou",
  docgen: "gerou documentação",
  approve: "aprovou",
  reject: "rejeitou",
  deactivate: "inativou",
  view_other: "abriu de outro",
  blocked: "bloqueado (403)",
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}
function fmtTokens(n: number): string {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}
const MODE_PT: Record<string, string> = { business: "Negócio", technical: "Técnico" };
const STATUS_PT: Record<string, string> = {
  pending: "pendente",
  approved: "aprovado",
  rejected: "rejeitado",
  inactive: "inativa",
};

type AdminTab = "overview" | "users" | "billing" | "branding" | "instructions" | "repos" | "organizations" | "plans" | "sso" | "compliance" | "audit" | "api" | "engines";

/** Critérios de ordenação da lista de usuários. */
type UserSortKey = "createdAt" | "lastActivityAt" | "name" | "email" | "sessionCount" | "totalCostUsd";

const USER_SORT_LABEL: Record<UserSortKey, string> = {
  createdAt: "Últimos cadastrados",
  lastActivityAt: "Últimos utilizadores",
  name: "Nome (alfabética)",
  email: "E-mail (alfabética)",
  sessionCount: "Conversas",
  totalCostUsd: "Gasto (US$)",
};



/** Ordenação escolhida sobrevive a recarregar a página (localStorage). */
const USER_SORT_STORAGE_KEY = "ea_admin_users_sort";

function readStoredUserSort(): { key: UserSortKey; asc: boolean } {
  try {
    const raw = localStorage.getItem(USER_SORT_STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as { key?: string; asc?: boolean };
      if (saved.key && saved.key in USER_SORT_LABEL) {
        return { key: saved.key as UserSortKey, asc: saved.asc === true };
      }
    }
  } catch {
    /* storage indisponível/corrompido - usa o padrão */
  }
  return { key: "createdAt", asc: false };
}

/**
 * Comparador por critério, sempre em ordem CRESCENTE - a direção inverte no
 * chamador (quem nunca usou vai pro fim lá, antes da inversão).
 */
function compareUsers(a: AdminUserRow, b: AdminUserRow, key: UserSortKey): number {
  switch (key) {
    case "name":
      return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
    case "email":
      return a.email.localeCompare(b.email, "pt-BR", { sensitivity: "base" });
    case "createdAt":
      return a.createdAt.localeCompare(b.createdAt);
    case "lastActivityAt":
      return (a.lastActivityAt ?? "").localeCompare(b.lastActivityAt ?? "");
    case "sessionCount":
      return a.sessionCount - b.sessionCount;
    case "totalCostUsd":
      return a.totalCostUsd - b.totalCostUsd;
  }
}

const NAV_ICON: Record<AdminTab, React.ReactNode> = {
  overview: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </svg>
  ),
  users: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="6" cy="5.5" r="2.3" />
      <path d="M1.5 14c0-2.6 2-4.2 4.5-4.2s4.5 1.6 4.5 4.2" />
      <path d="M10.5 3.2c1.1.3 1.9 1.3 1.9 2.4 0 1.1-.8 2.1-1.9 2.4" />
      <path d="M12 9.9c1.9.4 3 1.7 3 4.1" />
    </svg>
  ),
  billing: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.3" />
      <path d="M1.5 6.5h13" />
      <path d="M4 10h3" />
    </svg>
  ),
  branding: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7.5v3.5M8 5h.01" />
    </svg>
  ),
  instructions: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="1.5" width="10" height="13" rx="1.3" />
      <path d="M5.5 5h5M5.5 8h5M5.5 11h3" />
    </svg>
  ),
  sso: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="5.5" cy="10.5" r="2.7" />
      <path d="M7.4 8.6 12.5 3.5M12.5 3.5v2.3M12.5 3.5h-2.3" />
    </svg>
  ),
  audit: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </svg>
  ),
  compliance: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 1.5l5.5 2.5v4c0 3.2-2.3 5.6-5.5 6.5C4.8 13.6 2.5 11.2 2.5 8V4L8 1.5z" />
      <path d="M6 8l1.5 1.5L10.5 6.5" />
    </svg>
  ),
  api: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5.5 4 2 8l3.5 4M10.5 4 14 8l-3.5 4" />
    </svg>
  ),
  organizations: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 14V3h6v11M9 14V6.5h4V14M1.5 14h13" />
      <path d="M4.5 5.5h1M4.5 8h1M4.5 10.5h1" />
    </svg>
  ),
  repos: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="4.5" cy="3.5" r="1.8" />
      <circle cx="4.5" cy="12.5" r="1.8" />
      <circle cx="11.5" cy="6" r="1.8" />
      <path d="M4.5 5.3v5.4M11.5 7.8c0 2.4-3 2.4-5 3.2" />
    </svg>
  ),
  engines: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.4 1.4M11.6 11.6 13 13M13 3l-1.4 1.4M4.4 11.6 3 13" />
    </svg>
  ),
  plans: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 4.5h12M2 8h12M2 11.5h7" />
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" />
    </svg>
  ),
};

/** Org escolhida no seletor do sysadmin - persiste entre reloads; limpa no logout. */
export const ADMIN_ORG_SCOPE_KEY = "ea_admin_org_scope";

const ADMIN_TABS: AdminTab[] = ["overview", "users", "billing", "branding", "instructions", "repos", "organizations", "plans", "sso", "compliance", "audit", "api", "engines"];
/** Abas restritas ao operador da plataforma (o backend recusa as chamadas de qualquer forma). */
const SYSADMIN_TABS: AdminTab[] = ["organizations", "plans", "engines"];

export function AdminPage({ onClose, onOpenSession, selectedUserId, onSelectUser, isSysadmin }: Props) {
  const [tab, setTabState] = useState<AdminTab>(() => {
    // Deep-link: cada aba é um LINK próprio (/admin?tab=repos) que abre e
    // recarrega direto nela. Aba sysadmin pedida por quem não é cai no overview.
    const t = new URLSearchParams(window.location.search).get("tab") as AdminTab | null;
    if (t && ADMIN_TABS.includes(t) && !(SYSADMIN_TABS.includes(t) && !isSysadmin)) return t;
    return "overview";
  });
  /** Troca de aba mantém a URL sincronizada - copiar/recarregar preserva a tela. */
  const setTab = useCallback((t: AdminTab) => {
    setTabState(t);
    const url = t === "overview" ? "/admin" : `/admin?tab=${t}`;
    window.history.replaceState({}, "", url);
  }, []);
  // URL mudou com a página JÁ montada (voltar/avançar ou o tour navegando):
  // sincroniza a aba com o ?tab= atual.
  useEffect(() => {
    const syncFromUrl = () => {
      if (!window.location.pathname.startsWith("/admin")) return;
      const t = new URLSearchParams(window.location.search).get("tab") as AdminTab | null;
      const target = t && ADMIN_TABS.includes(t) && !(SYSADMIN_TABS.includes(t) && !isSysadmin) ? t : "overview";
      setTabState(target);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [isSysadmin]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  // ordenação da lista de usuários (critério + direção) e paginação (10/página)
  const [userSort, setUserSort] = useState<UserSortKey>(() => readStoredUserSort().key);
  const [userSortAsc, setUserSortAsc] = useState(() => readStoredUserSort().asc);
  const [userPage, setUserPage] = useState(1);
  useEffect(() => {
    // mudou busca/ordenação: volta pra 1ª página (a atual pode nem existir mais)
    setUserPage(1);
  }, [userSearch, userSort, userSortAsc]);
  useEffect(() => {
    try {
      localStorage.setItem(USER_SORT_STORAGE_KEY, JSON.stringify({ key: userSort, asc: userSortAsc }));
    } catch {
      /* quota/privado - segue só em memória */
    }
  }, [userSort, userSortAsc]);

  // Seletor de org (sysadmin só) - sem isto, o operador da plataforma só
  // conseguia configurar billing/SSO/compliance/API/identidade da PRÓPRIA
  // org, nunca a de um cliente.
  const [allOrgs, setAllOrgs] = useState<{ id: string; name: string; logoDataUrl?: string | null }[]>([]);
  // A org escolhida no seletor SOBREVIVE a recarregar a página (localStorage) -
  // só reseta no logout (useAuth limpa a chave) ou se a org sumir da lista.
  const [targetOrgId, setTargetOrgIdState] = useState(() => {
    if (!isSysadmin) return "";
    try {
      return localStorage.getItem(ADMIN_ORG_SCOPE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const setTargetOrgId = useCallback((id: string) => {
    setTargetOrgIdState(id);
    try {
      if (id) localStorage.setItem(ADMIN_ORG_SCOPE_KEY, id);
      else localStorage.removeItem(ADMIN_ORG_SCOPE_KEY);
    } catch {
      /* storage indisponível - segue só em memória */
    }
  }, []);
  useEffect(() => {
    if (!isSysadmin) return;
    fetch("/api/admin/organizations")
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j: { items?: { id: string; name: string }[] }) => {
        const items = j.items ?? [];
        setAllOrgs(items);
        // org gravada não existe mais (removida/outra instância): volta pra própria
        setTargetOrgIdState((cur) => (cur && !items.some((o) => o.id === cur) ? "" : cur));
      })
      .catch(() => {});
  }, [isSysadmin]);
  const orgIdOverride = targetOrgId || undefined;

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const r = await fetch("/api/admin/users");
      if (r.ok) setUsers((await r.json()) as AdminUserRow[]);
    } finally {
      setUsersLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const selected = users.find((u) => u.id === selectedUserId) ?? null;

  const updateUserRow = useCallback((updated: AdminUserRow) => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
  }, []);

  const changeRole = useCallback(
    async (u: AdminUserRow, role: "admin" | "user") => {
      setBusyId(u.id);
      try {
        const r = await fetch(`/api/admin/users/${encodeURIComponent(u.id)}/role`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ role }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) {
          showToast(j.error ?? "Falha ao alterar o papel.", "error");
          return;
        }
        updateUserRow({ ...u, role: j.user.role });
      } finally {
        setBusyId(null);
      }
    },
    [updateUserRow],
  );

  const fetchSessions = useCallback(
    async (offset: number): Promise<Page<SessionSummary>> => {
      if (!selectedUserId) return { items: [], hasMore: false };
      const r = await fetch(
        `/api/admin/users/${encodeURIComponent(selectedUserId)}/sessions?offset=${offset}&limit=10`,
      );
      if (!r.ok) return { items: [], hasMore: false };
      return (await r.json()) as Page<SessionSummary>;
    },
    [selectedUserId],
  );
  const sessionsInf = useInfinite<SessionSummary>(fetchSessions, [selectedUserId]);

  const fetchActivity = useCallback(
    async (offset: number): Promise<Page<ActivityEntry>> => {
      if (!selectedUserId) return { items: [], hasMore: false };
      const r = await fetch(
        `/api/admin/activity?userId=${encodeURIComponent(selectedUserId)}&offset=${offset}&limit=30`,
      );
      if (!r.ok) return { items: [], hasMore: false };
      return (await r.json()) as Page<ActivityEntry>;
    },
    [selectedUserId],
  );
  const activityInf = useInfinite<ActivityEntry>(fetchActivity, [selectedUserId]);

  // confirmações customizadas (nenhum diálogo nativo do navegador)
  const [confirmAct, setConfirmAct] = useState<{ user: AdminUserRow; action: "reject" | "deactivate" | "reset-link" } | null>(null);
  const [gdprTarget, setGdprTarget] = useState<AdminUserRow | null>(null);

  const runAct = useCallback(
    async (u: AdminUserRow, action: "approve" | "reject" | "deactivate" | "reset-link") => {
      setBusyId(u.id);
      try {
        await fetch(`/api/admin/users/${encodeURIComponent(u.id)}/${action}`, { method: "POST" });
        await loadUsers();
      } finally {
        setBusyId(null);
      }
    },
    [loadUsers],
  );

  const act = useCallback(
    (u: AdminUserRow, action: "approve" | "reject" | "deactivate" | "reset-link") => {
      if (action === "approve") {
        void runAct(u, action);
        return;
      }
      setConfirmAct({ user: u, action });
    },
    [runAct],
  );

  /** Exclusão LGPD (epico 10) - irreversível, exige digitar o e-mail da pessoa. */
  const gdprDelete = useCallback(
    async (u: AdminUserRow, typed: string) => {
      if (typed.trim() !== u.email) {
        showToast("E-mail não confere - nada foi apagado.", "error");
        return;
      }
      setBusyId(u.id);
      try {
        const r = await fetch(`/api/admin/users/${encodeURIComponent(u.id)}/gdpr-delete`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ confirm: typed }),
        });
        if (r.ok) {
          onSelectUser(null);
          await loadUsers();
        } else {
          const j = await r.json().catch(() => ({}));
          showToast(j.error ?? "Falha ao excluir.", "error");
        }
      } finally {
        setBusyId(null);
      }
    },
    [loadUsers, onSelectUser],
  );

  const pending = users.filter((u) => u.status === "pending");
  const search = userSearch.trim().toLowerCase();
  const sortDir = userSortAsc ? 1 : -1;
  const others = users
    .filter((u) => u.status !== "pending")
    .filter((u) => !search || u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search))
    .sort((a, b) => {
      // quem nunca usou fica no FIM na ordenação por utilização, suba ou desça
      if (userSort === "lastActivityAt") {
        if (!a.lastActivityAt && !b.lastActivityAt) return 0;
        if (!a.lastActivityAt) return 1;
        if (!b.lastActivityAt) return -1;
      }
      return compareUsers(a, b, userSort) * sortDir;
    });
  const userPageCount = Math.max(1, Math.ceil(others.length / PAGE_SIZE));
  const userPageSafe = Math.min(userPage, userPageCount);
  const pagedUsers = others.length > PAGE_SIZE ? others.slice((userPageSafe - 1) * PAGE_SIZE, userPageSafe * PAGE_SIZE) : others;

  const navItem = (key: AdminTab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      className={`admin-nav-item ${tab === key ? "active" : ""}`}
      onClick={() => setTab(key)}
      data-tour={`admin-nav-${key}`}
    >
      {NAV_ICON[key]}
      {label}
      {key === "users" && pending.length > 0 && <span className="badge-admin" style={{ marginLeft: "auto" }}>{pending.length}</span>}
    </button>
  );

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <h1>Administração</h1>
          <p className="admin-sub">Aprovação de contas, configurações, conversas e logs dos usuários.</p>
        </div>
        <button className="admin-close" onClick={onClose} type="button">
          Voltar ao chat
        </button>
      </div>

      <div className="admin-shell">
        <nav className="admin-nav" role="tablist" aria-label="Seções da administração">
          {isSysadmin && allOrgs.length > 0 && (
            <OrgSwitcher orgs={allOrgs} value={targetOrgId} onChange={setTargetOrgId} />
          )}

          <span className="admin-nav-group-label">Organização</span>
          {navItem("overview", "Visão geral")}
          {navItem("users", "Usuários")}
          {navItem("billing", "Plano e billing")}
          {navItem("branding", "Identidade")}
          {navItem("instructions", "Instruções")}
          {navItem("repos", "Repositórios")}
          {navItem("sso", "SSO")}
          {navItem("compliance", "Compliance")}
          {navItem("audit", "Auditoria")}
          {navItem("api", "API pública")}

          {isSysadmin && (
            <>
              <div className="admin-nav-divider" />
              <span className="admin-nav-group-label">Plataforma</span>
              {navItem("organizations", "Organizações")}
              {navItem("plans", "Planos")}
              {navItem("engines", "Motores de IA")}
            </>
          )}
        </nav>

        <div>
          {tab === "overview" ? (
            <OverviewPanel isSysadmin={isSysadmin} orgId={orgIdOverride} onNavigate={(t) => setTab(t as AdminTab)} />
          ) : tab === "branding" ? (
            isSysadmin ? (
              orgIdOverride ? (
                <OrgIdentityPanel orgId={orgIdOverride} />
              ) : (
                <BrandingSettings />
              )
            ) : (
              <OrgIdentityPanel />
            )
          ) : tab === "billing" ? (
            <BillingPanel orgId={orgIdOverride} isSysadmin={isSysadmin} />
          ) : tab === "instructions" ? (
            <InstructionsPanel orgId={orgIdOverride} isSysadmin={isSysadmin} />
          ) : tab === "organizations" && isSysadmin ? (
            <OrganizationsPanel
              onManageOrg={(id) => {
                setTargetOrgId(id);
                setTab("overview");
              }}
            />
          ) : tab === "sso" ? (
            <SsoPanel orgId={orgIdOverride} />
          ) : tab === "compliance" ? (
            <RetentionPanel orgId={orgIdOverride} />
          ) : tab === "audit" ? (
            <AuditPanel orgId={orgIdOverride} />
          ) : tab === "api" ? (
            <ApiPanel orgId={orgIdOverride} />
          ) : tab === "repos" ? (
            <ReposPage embedded orgId={orgIdOverride} isSysadmin={isSysadmin} onClose={() => setTab("overview")} />
          ) : tab === "plans" && isSysadmin ? (
            <PlansPanel />
          ) : tab === "engines" && isSysadmin ? (
            <EngineAuthPanel />
          ) : (
      <div className="admin-body">
        <section className="admin-col">
          <InvitesPanel orgId={orgIdOverride} />
          {usersLoading ? (
            <div className="admin-block">
              <SkeletonList rows={8} />
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <div className="admin-block">
                  <h2>Aguardando aprovação ({pending.length})</h2>
                  {pending.map((u) => (
                    <div key={u.id} className="admin-user pending">
                      <div className="admin-user-info">
                        <span className="admin-user-name">{u.name}</span>
                        <span className="admin-user-email">{u.email}</span>
                        <span className="admin-user-meta">criado {fmtDate(u.createdAt)}</span>
                      </div>
                      <div className="admin-user-actions">
                        <button className="btn-approve" disabled={busyId === u.id} onClick={() => act(u, "approve")} type="button">
                          Aprovar
                        </button>
                        <button className="btn-reject" disabled={busyId === u.id} onClick={() => act(u, "reject")} type="button">
                          Rejeitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="admin-block">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                  <h2 style={{ margin: 0 }}>Usuários ({others.length})</h2>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5 }}>
                      <span className="admin-user-meta">Ordenar por</span>
                      <select
                        className="select-sm"
                        value={userSort}
                        onChange={(e) => setUserSort(e.target.value as UserSortKey)}
                        aria-label="Critério de ordenação"
                      >
                        {(Object.keys(USER_SORT_LABEL) as UserSortKey[]).map((k) => (
                          <option key={k} value={k}>
                            {USER_SORT_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <select
                      className="sort-dir-select"
                      value={userSortAsc ? "asc" : "desc"}
                      onChange={(e) => setUserSortAsc(e.target.value === "asc")}
                      title={userSortAsc ? "Ordem crescente" : "Ordem decrescente"}
                      aria-label="Direção da ordenação"
                    >
                      <option value="desc">↓</option>
                      <option value="asc">↑</option>
                    </select>
                    <input
                      type="search"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Buscar por nome ou e-mail…"
                      style={{ maxWidth: 220, padding: "5px 8px", fontSize: 12.5 }}
                    />
                  </div>
                </div>
                {others.length === 0 ? (
                  <div className="admin-empty small">Nenhum usuário encontrado.</div>
                ) : (
                pagedUsers.map((u) => (
                  <div
                    key={u.id}
                    className={`admin-user ${selectedUserId === u.id ? "selected" : ""}`}
                    onClick={() => onSelectUser(u.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="admin-user-info">
                      <span className="admin-user-name">
                        {u.name}
                        {u.role !== "user" && (
                          <span className="badge-admin">{u.role === "sysadmin" ? "sysadmin" : "admin"}</span>
                        )}
                        {u.status === "rejected" && <span className="badge-rejected">rejeitado</span>}
                        {u.status === "inactive" && <span className="badge-rejected">inativa</span>}
                      </span>
                      <span className="admin-user-email">{u.email}</span>
                      <span className="admin-user-meta">
                        {u.sessionCount} conversa(s) · {fmtTokens(u.totalTokens)} tokens · ${u.totalCostUsd.toFixed(3)}
                        {u.lastActivityAt ? ` · último uso ${fmtDate(u.lastActivityAt)}` : " · nunca usou"}
                      </span>
                    </div>
                    <div className="admin-user-actions">
                      <button
                        className="btn-reject"
                        disabled={busyId === u.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          act(u, "reset-link");
                        }}
                        title="Envia o link de redefinição por E-MAIL (Brevo) para esta pessoa"
                        type="button"
                      >
                        Enviar link de senha
                      </button>
                      {u.status === "approved" && u.role === "user" && (
                        <button
                          className="btn-reject"
                          disabled={busyId === u.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            act(u, "deactivate");
                          }}
                          type="button"
                        >
                          Inativar
                        </button>
                      )}
                      {(u.status === "rejected" || u.status === "inactive") && (
                        <button
                          className="btn-approve"
                          disabled={busyId === u.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            act(u, "approve");
                          }}
                          type="button"
                        >
                          Reativar
                        </button>
                      )}
                    </div>
                  </div>
                ))
                )}
                <Pagination page={userPageSafe} pageCount={userPageCount} onChange={setUserPage} label={`${others.length} usuário(s)`} />
              </div>
            </>
          )}
        </section>

        <section className="admin-col admin-detail">
          {!selected ? (
            <div className="admin-empty">Selecione um usuário para ver configurações, conversas e logs.</div>
          ) : (
            <>
              <h2>
                {selected.name} <span className="admin-user-email">({selected.email})</span>
              </h2>

              <div className="admin-block">
                <h3>Perfil e configurações</h3>
                <dl className="admin-kv">
                  <div>
                    <dt>Papel</dt>
                    <dd>
                      {selected.role === "sysadmin" ? "Operador da plataforma" : selected.role === "admin" ? "Administrador da org" : "Usuário"}
                      {selected.role !== "sysadmin" && (
                        <button
                          className="btn-reject"
                          style={{ marginLeft: 8, padding: "2px 8px", fontSize: 11 }}
                          disabled={busyId === selected.id}
                          onClick={() => void changeRole(selected, selected.role === "admin" ? "user" : "admin")}
                          type="button"
                        >
                          {selected.role === "admin" ? "Rebaixar pra usuário" : "Promover a admin"}
                        </button>
                      )}
                    </dd>
                  </div>
                  <div><dt>Status</dt><dd>{STATUS_PT[selected.status] ?? selected.status}</dd></div>
                  <div><dt>Criado em</dt><dd>{fmtDate(selected.createdAt)}</dd></div>
                  {selected.approvedAt && <div><dt>Aprovado em</dt><dd>{fmtDate(selected.approvedAt)}</dd></div>}
                  <div><dt>Modo padrão</dt><dd>{MODE_PT[selected.settings.defaultMode] ?? selected.settings.defaultMode}</dd></div>
                  <div><dt>Modelo padrão</dt><dd>{selected.settings.defaultModel || "Padrão do servidor"}</dd></div>
                  <div><dt>Conversas</dt><dd>{selected.sessionCount}</dd></div>
                  <div><dt>Tokens</dt><dd>{fmtTokens(selected.totalTokens)}</dd></div>
                  <div><dt>Custo total</dt><dd>${selected.totalCostUsd.toFixed(4)}</dd></div>
                </dl>
              </div>

              <AdminNoteEditor
                key={`note-${selected.id}`}
                user={selected}
                onSaved={(note) => updateUserRow({ ...selected, adminNote: note })}
              />

              <UserPermissionsForm key={selected.id} user={selected} onUpdated={updateUserRow} />

              {selected.role !== "sysadmin" && (
                <div className="admin-block">
                  <h3>LGPD</h3>
                  <p className="admin-user-meta" style={{ marginBottom: 8 }}>
                    Exclusão de dados a pedido do titular - remove acesso, conversas e atividade
                    permanentemente. Não afeta a identidade compartilhada (e-mail/senha) com outros apps.
                  </p>
                  <button
                    className="btn-reject"
                    disabled={busyId === selected.id}
                    onClick={() => setGdprTarget(selected)}
                    type="button"
                  >
                    Excluir dados (LGPD)
                  </button>
                </div>
              )}

              <div className="admin-block">
                <h3>Conversas</h3>
                {sessionsInf.initialLoading ? (
                  <SkeletonList rows={4} />
                ) : sessionsInf.items.length === 0 ? (
                  <div className="admin-empty small">Nenhuma conversa.</div>
                ) : (
                  <>
                    {sessionsInf.items.map((s) => (
                      <a
                        key={s.id}
                        className="admin-session"
                        href={`/?s=${encodeURIComponent(s.id)}`}
                        onClick={(e) => {
                          e.preventDefault();
                          onOpenSession(s.id);
                        }}
                        title="Abrir conversa"
                      >
                        <span className="admin-session-title">
                          {s.title}
                          {s.source === "api" && (
                            <span className="session-source-badge" style={{ marginLeft: 6 }} title="Conversa criada via API">
                              API
                            </span>
                          )}
                          {s.deleted && (
                            <span
                              className="session-source-badge"
                              style={{ marginLeft: 6, background: "var(--danger, #d64545)", color: "#fff" }}
                              title={s.deletedAt ? `Apagada em ${fmtDate(s.deletedAt)}` : "Conversa apagada pelo usuário"}
                            >
                              apagada
                            </span>
                          )}
                        </span>
                        <span className="admin-session-meta">
                          {s.turnCount} msgs · {fmtTokens(s.totalTokens ?? 0)} tokens · {fmtDate(s.updatedAt)}
                        </span>
                      </a>
                    ))}
                    <div ref={sessionsInf.sentinelRef} className="infinite-sentinel" aria-hidden />
                    {sessionsInf.loadingMore && <SkeletonList rows={2} />}
                  </>
                )}
              </div>

              <div className="admin-block">
                <h3>Atividade recente</h3>
                {activityInf.initialLoading ? (
                  <SkeletonList rows={5} />
                ) : activityInf.items.length === 0 ? (
                  <div className="admin-empty small">Sem registros.</div>
                ) : (
                  <div className="admin-activity">
                    {activityInf.items.map((a, i) => {
                      const isCross = a.type === "view_other" || a.type === "blocked";
                      const tokensSuffix =
                        typeof a.tokens === "number" && a.tokens > 0 ? ` · ${fmtTokens(a.tokens)} tokens` : "";
                      const plainDetail =
                        (a.preview ? `"${a.preview}"` : a.detail ? a.detail : a.model ? a.model : "") + tokensSuffix;
                      return (
                        <div key={`${a.ts}-${i}`} className="admin-act-row">
                          <span className="admin-act-ts">{fmtDate(a.ts)}</span>
                          <span className={`admin-act-type type-${a.type}`}>{ACTIVITY_LABEL[a.type]}</span>
                          {isCross && a.sessionId ? (
                            <span className="admin-act-detail">
                              a conversa{" "}
                              <a
                                className="admin-act-link"
                                href={`/?s=${encodeURIComponent(a.sessionId)}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (a.sessionId) onOpenSession(a.sessionId);
                                }}
                                title="Abrir esta conversa"
                              >
                                {a.sessionTitle || "sem título"}
                              </a>
                              {a.ownerEmail ? ` de ${a.ownerEmail}` : ""}
                            </span>
                          ) : a.sessionId ? (
                            <a
                              className="admin-act-detail admin-act-link"
                              href={`/?s=${encodeURIComponent(a.sessionId)}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (a.sessionId) onOpenSession(a.sessionId);
                              }}
                              title="Abrir esta conversa"
                            >
                              {plainDetail || "abrir conversa"}
                            </a>
                          ) : (
                            <span className="admin-act-detail">{plainDetail}</span>
                          )}
                        </div>
                      );
                    })}
                    <div ref={activityInf.sentinelRef} className="infinite-sentinel" aria-hidden />
                    {activityInf.loadingMore && <SkeletonList rows={2} />}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
      )}
        </div>
      </div>

      {confirmAct && (
        <ConfirmModal
          title={
            confirmAct.action === "reject"
              ? "Rejeitar acesso"
              : confirmAct.action === "deactivate"
                ? "Inativar conta"
                : "Enviar link de senha"
          }
          message={
            confirmAct.action === "reject"
              ? `Rejeitar o acesso de ${confirmAct.user.email}?`
              : confirmAct.action === "deactivate"
                ? `Inativar a conta de ${confirmAct.user.email}? Ela perde o acesso imediatamente.`
                : `Enviar o link de redefinição de senha para ${confirmAct.user.email}? Vai por e-mail e vale por 1 hora; a senha atual continua valendo até o link ser usado.`
          }
          confirmLabel={
            confirmAct.action === "reject" ? "Rejeitar" : confirmAct.action === "deactivate" ? "Inativar" : "Enviar link"
          }
          onConfirm={() => void runAct(confirmAct.user, confirmAct.action)}
          onClose={() => setConfirmAct(null)}
        />
      )}
      {gdprTarget && (
        <PromptModal
          title="Excluir dados (LGPD)"
          label={`Isto apaga permanentemente o acesso, as conversas e a atividade de ${gdprTarget.email}. Não pode ser desfeito. Digite o e-mail da pessoa pra confirmar:`}
          placeholder={gdprTarget.email}
          submitLabel="Excluir permanentemente"
          danger
          onSubmit={(value) => void gdprDelete(gdprTarget, value)}
          onClose={() => setGdprTarget(null)}
        />
      )}
    </div>
  );
}
